import { useCallback, useEffect, useRef, useState } from 'react';
import type { StagedFile } from '@securspace/securspace-ui-kit';

export const CHECK_IN_IMAGE_MAX_FILES = 10;
export const CHECK_IN_IMAGE_MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024;
export const CHECK_IN_IMAGE_ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/gif'];

export interface CheckInImageValidationError {
  code: 'INVALID_FILE_TYPE' | 'FILE_TOO_LARGE' | 'MAX_FILES_EXCEEDED';
  message: string;
}

interface UseCheckInImageUploadResult {
  stagedFiles: StagedFile[];
  addFiles: (files: File[]) => CheckInImageValidationError[];
  removeFile: (file: StagedFile) => void;
  /**
   * Replaces a staged file's `previewUrl` (e.g. after a crop) in place.
   * Registers the new blob URL for ownership immediately, and defers
   * revocation of the superseded URL to a post-commit effect so the
   * rendered state always reflects the new URL before the old one is
   * revoked (prevents a broken-image flash).
   */
  replaceFile: (file: StagedFile, updated: StagedFile) => void;
  clearFiles: () => void;
}

/**
 * Owns the selected check-in images and every object URL created for their previews.
 * Files are compared by identity so previews remain stable across form re-renders.
 */
export const useCheckInImageUpload = (): UseCheckInImageUploadResult => {
  const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([]);
  const stagedFilesRef = useRef<StagedFile[]>([]);
  const previewUrlsRef = useRef<Set<string>>(new Set());
  const pendingRevocationsRef = useRef<string[]>([]);

  const updateStagedFiles = useCallback((next: StagedFile[]) => {
    stagedFilesRef.current = next;
    setStagedFiles(next);
  }, []);

  const addFiles = useCallback((files: File[]): CheckInImageValidationError[] => {
    const errors: CheckInImageValidationError[] = [];
    const accepted: StagedFile[] = [];

    files.forEach((file) => {
      if (!CHECK_IN_IMAGE_ACCEPTED_TYPES.includes(file.type)) {
        errors.push({
          code: 'INVALID_FILE_TYPE',
          message: 'Unsupported file type. Please upload a PNG, JPG or GIF image.',
        });
        return;
      }

      if (file.size > CHECK_IN_IMAGE_MAX_FILE_SIZE_BYTES) {
        errors.push({
          code: 'FILE_TOO_LARGE',
          message: 'That image is too large. Please choose a file under 20 MB.',
        });
        return;
      }

      if (stagedFilesRef.current.length + accepted.length >= CHECK_IN_IMAGE_MAX_FILES) {
        errors.push({
          code: 'MAX_FILES_EXCEEDED',
          message: `You can add up to ${CHECK_IN_IMAGE_MAX_FILES} images total.`,
        });
        return;
      }

      const previewUrl = URL.createObjectURL(file);
      previewUrlsRef.current.add(previewUrl);
      accepted.push({ file, previewUrl });
    });

    if (accepted.length > 0) {
      updateStagedFiles([...stagedFilesRef.current, ...accepted]);
    }

    return errors;
  }, [updateStagedFiles]);

  const removeFile = useCallback((fileToRemove: StagedFile) => {
    if (previewUrlsRef.current.delete(fileToRemove.previewUrl)) {
      URL.revokeObjectURL(fileToRemove.previewUrl);
    }
    updateStagedFiles(
      stagedFilesRef.current.filter((stagedFile) => stagedFile.file !== fileToRemove.file)
    );
  }, [updateStagedFiles]);

  // Commits the replacement URL to staged state first, and only revokes the
  // superseded URL in the effect below (after that state has committed and
  // painted), preventing a broken-image flash on the previously-displayed URL.
  const replaceFile = useCallback((file: StagedFile, updated: StagedFile) => {
    const stillPresent = stagedFilesRef.current.some((staged) => staged.file === file.file);
    if (!stillPresent) return;

    previewUrlsRef.current.add(updated.previewUrl);
    updateStagedFiles(
      stagedFilesRef.current.map((staged) => (staged.file === file.file ? updated : staged))
    );

    if (file.previewUrl !== updated.previewUrl) {
      pendingRevocationsRef.current.push(file.previewUrl);
    }
  }, [updateStagedFiles]);

  // Runs after every render/commit and only acts when there is something
  // queued, so it's a no-op on unrelated re-renders and on unmount (the
  // separate unmount effect below owns final cleanup of all owned URLs).
  useEffect(() => {
    const pendingUrls = pendingRevocationsRef.current.splice(0);
    pendingUrls.forEach((previewUrl) => {
      if (previewUrlsRef.current.delete(previewUrl)) {
        URL.revokeObjectURL(previewUrl);
      }
    });
  });

  const clearFiles = useCallback(() => {
    previewUrlsRef.current.forEach((previewUrl) => URL.revokeObjectURL(previewUrl));
    previewUrlsRef.current.clear();
    updateStagedFiles([]);
  }, [updateStagedFiles]);

  useEffect(
    () => () => {
      previewUrlsRef.current.forEach((previewUrl) => URL.revokeObjectURL(previewUrl));
      previewUrlsRef.current.clear();
    },
    []
  );

  return { stagedFiles, addFiles, removeFile, replaceFile, clearFiles };
};

